import { useContext, useMemo } from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxControlSurface,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@utilities-one/gpm-ui';

import { OptionalPortalContainerContext } from '@/library/portal/OptionalPortalContainerContext';

/**
 * Presentational single-select autocomplete over a static item list (Base UI
 * Combobox with its built-in client-side filtering). The selected value
 * renders as plain text in the input — no chip. Controlled by an item key:
 * `value`/`onChange` speak keys, `getKey`/`getLabel` adapt any item shape.
 * `onChange(null)` reports a cleared selection — callers decide whether to
 * honor it. Portals into `OptionalPortalContainerContext` so the list stays
 * clickable inside dialogs. Consumed by the `FormAutocomplete` form-kit
 * adapter and directly by controlled callers (job wizard).
 */

interface Props<T> {
  id: string;
  items: T[];
  /** Key of the selected item, or null for no selection. */
  value: string | null;
  onChange: (key: string | null) => void;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Input `name` — the form-kit adapter passes the field name. */
  name?: string;
  onBlur?: () => void;
}

export function AutocompleteCombobox<T>({
  id,
  items,
  value,
  onChange,
  getKey,
  getLabel,
  placeholder = 'Select an option',
  emptyText = 'No items found.',
  disabled = false,
  invalid = false,
  name,
  onBlur,
}: Props<T>) {
  const portalHostEl = useContext(OptionalPortalContainerContext);

  /** `undefined` means "uncontrolled" to Base UI; keep `null` so the combobox stays controlled for its whole lifetime. */
  const selectedItem = useMemo(() => items.find((item) => getKey(item) === value) ?? null, [items, value]);

  return (
    <Combobox
      items={items}
      disabled={disabled}
      itemToStringLabel={(item) => (item == null ? '' : getLabel(item as T))}
      itemToStringValue={(item) => (item == null ? '' : getLabel(item as T))}
      isItemEqualToValue={(a, b) => (a == null ? null : getKey(a as T)) === (b == null ? null : getKey(b as T))}
      value={selectedItem}
      onValueChange={(next) => {
        const nextKey = next == null ? null : getKey(next as T);
        // Base UI re-reports a re-picked option; only real changes reach `onChange` (callers reset dependent fields on it).
        if (nextKey !== (value || null)) onChange(nextKey);
      }}
    >
      <ComboboxControlSurface>
        <ComboboxInput
          id={id}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid}
          onBlur={onBlur}
          className="w-full"
        />
      </ComboboxControlSurface>
      <ComboboxContent portalContainer={portalHostEl ?? undefined}>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => (
            <ComboboxItem key={getKey(item)} value={item}>
              {getLabel(item)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
